import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Indicator, ModalProps } from '@hhgtech/hhg-components/mantine'
import { Close } from '@hhgtech/icons/core'
import {
  Box,
  BoxProps,
  Button,
  Popover,
  Portal,
  Transition,
} from '@mantine/core'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { MANTINE_MODAL_TARGET } from 'interfaces/constants'

import useStyles from './index.styles'
import { AIChatBotContext } from '../context'
import { TypeWritingEffect } from '../typeWritingEffect'
import { Widget, WidgetProps } from '../widget'
import { AvatarFocus } from '../widget/avatarFocus'

export type BubbleChatProps = {
  widgetModalProps?: Partial<ModalProps>
} & BoxProps

// const HASH_TO_OPEN = '#hello-sehat-expert'

const BubbleChat = ({
  styles,
  classNames,
  widgetModalProps,
  ...props
}: BubbleChatProps) => {
  const isMobile = useIsMobile()

  const [showChatIcon, setShowChatIcon] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [startWriting, setStartWriting] = useState(false)
  const [showMiniIcon, setShowMiniIcon] = useState(false)
  const [countOpen, setCountOpen] = useState(0)
  const bgImage = CommonGAssets.getAssetPath('soya/chat-bubble.png')
  const autoShowTimer = useRef<ReturnType<typeof setTimeout>>()
  // const firstOpen = useRef(true)
  const {
    // avatar,
    showWidget,
    setShowWidget,
    expandWidget,
    setExpandWidget,
    autoPopupTimes = 2,
  } = useContext(AIChatBotContext)
  const { classes } = useStyles(
    {
      showAnimation: showChatIcon && showMiniIcon && !showWidget,
      bgImage,
      expandWidget,
    },
    {
      name: 'AIChatBot__BubbleChat',
      styles,
      classNames,
    },
  )
  const currentUrl = useCurrentUrl()

  const widgetProps: Partial<WidgetProps> = {
    className: classes.widget,
    onClose: () => {
      setShowWidget(false)
      setCountOpen((v) => v + 1)
    },
  }

  useEffect(() => {
    setShowWidget(true)
    setShowChatIcon(true)
    // const newImage = new Image()
    // newImage.src = avatar
    // newImage.onload = () => {
    //   setShowChatIcon(true)
    //   if (window.location.hash === HASH_TO_OPEN) {
    //     setExpandWidget(true)
    //     setShowWidget(true)
    //   }
    // }
  }, [])

  useEffect(() => {
    showWidget && setShowWelcome(false)
  }, [showWidget])

  useEffect(() => {
    const newImage = new Image()
    newImage.src = bgImage
    newImage.onload = () => {
      setTimeout(() => setShowWelcome(true), 1000)
    }
  }, [showChatIcon])

  useEffect(() => {
    if (showWelcome) {
      setTimeout(() => setStartWriting(true), 1000)
    } else {
      showChatIcon && setShowMiniIcon(true)
    }
  }, [showWelcome])

  // useEffect(() => {
  //   if (startWriting) {
  //     if (window.location.hash !== HASH_TO_OPEN) {
  //       !firstOpen.current && setTimeout(() => setShowWidget(true), 2000 + 2000)
  //     }
  //   }
  // }, [startWriting])

  useEffect(() => {
    if (countOpen < autoPopupTimes) {
      isMobile && setExpandWidget(false)
      autoShowTimer.current = setTimeout(() => setShowWidget(true), 10000)
    }
    return () => {
      autoShowTimer.current && clearTimeout(autoShowTimer.current)
    }
  }, [countOpen])

  useEffect(() => {
    !isMobile && setExpandWidget(true)
  }, [isMobile])

  useEffect(() => {
    if (isMobile && showWidget && expandWidget) {
      document.body.style.overflow = 'hidden'
      document.body.classList.add('noscroll')
    } else {
      document.body.style.overflow = ''
      document.body.classList.remove('noscroll')
    }
  }, [isMobile, showWidget, expandWidget])

  useEffect(() => {
    function setLeftForRangeNumber() {
      setShowWidget(false)
    }
    isMobile &&
      window.addEventListener('scroll', setLeftForRangeNumber, {
        passive: true,
      })
    return () => {
      isMobile && window.removeEventListener('scroll', setLeftForRangeNumber)
    }
  }, [])

  const handleClickToOpen = useCallback(() => {
    setExpandWidget(true)
    setShowWidget(true)
    // firstOpen.current = true
  }, [])

  return (
    <Portal target={MANTINE_MODAL_TARGET}>
      <Box className={classes.root} {...props}>
        <Transition
          mounted={showChatIcon}
          keepMounted
          // transition={'slide-left'}
          transition={'fade'}
        >
          {(styles) => (
            <Popover
              opened={showWidget}
              onChange={setShowWidget}
              classNames={{
                dropdown: classes.popoverDropdown,
              }}
              keepMounted
              withinPortal
              portalProps={{
                target: MANTINE_MODAL_TARGET,
              }}
              width={
                isMobile && !expandWidget
                  ? 'calc(100% - 32px)'
                  : isMobile
                  ? '100%'
                  : undefined
              }
              position="bottom-end"
              closeOnClickOutside={false}
              transitionProps={{
                transition: 'pop-bottom-right',
              }}
            >
              <Popover.Target>
                <Box className={classes.wrapper} style={styles}>
                  <span className="ripple pinkBg"></span>
                  <span className="ripple pinkBg"></span>
                  <span className="ripple pinkBg"></span>
                  <Button
                    sx={{
                      height: 'auto',
                      padding: 0,
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <Indicator
                      disabled={!showMiniIcon}
                      inline
                      // processing
                      size={12}
                      classNames={{
                        common: classes.indicatorCommon,
                        indicator: classes.indicatorIndicator,
                      }}
                      style={styles}
                    >
                      <AvatarFocus
                        borderSize={4}
                        avatarSize={
                          isMobile || (!showWelcome && showMiniIcon) ? 64 : 80
                        }
                        className="pointer-event-child-none"
                        data-event-category="Chatbot"
                        data-event-action="Start Chat"
                        data-event-label={currentUrl}
                        onClick={handleClickToOpen}
                      />
                    </Indicator>
                  </Button>
                </Box>
              </Popover.Target>
              <Popover.Dropdown>
                <Widget {...widgetProps} />
              </Popover.Dropdown>
            </Popover>
          )}
        </Transition>
        <Transition
          mounted={showWelcome}
          keepMounted
          transition="pop-bottom-right"
        >
          {(styles) => (
            <Box className={classes.welcomeBox} style={styles}>
              <Box
                className="pointer-event-child-none"
                data-event-category="Chatbot"
                data-event-action="Start Chat"
                data-event-label={currentUrl}
                onClick={handleClickToOpen}
              >
                <Box className={classes.welcomeBoxInner}>
                  {startWriting && (
                    <TypeWritingEffect
                      className={classes.welcomeText}
                      size="s3"
                      text="Chat Hello Sehat Expert, Dapatkan Tips Tumbuh Kembang Si Kecil!"
                    />
                  )}
                </Box>
              </Box>
              <Close
                size={20}
                color="#fff"
                className={classes.welcomeClose}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowWelcome(false)
                }}
              />
            </Box>
          )}
        </Transition>
      </Box>
    </Portal>
  )
}

export { BubbleChat }
